import React, { useState, useRef, useEffect } from "react";
import { Box, Snackbar, Alert } from "@mui/material";
import * as monaco from 'monaco-editor';
import { useSnackbar } from "../../contexts/CustomSnackbarContext";
import MonacoEditorWithFullscreen from "components/Common/MonacoEditorWithFullscreen";
import { useTheme } from "contexts/ThemeContext";

// Define custom themes for JSON
monaco.editor.defineTheme("json-theme-light", {
    base: "vs",
    inherit: true,
    rules: [
        { token: "key", foreground: "A31515" },      // Red for keys
        { token: "value", foreground: "0451A5" },    // Blue for values
        { token: "number", foreground: "098658" },   // Green for numbers
        { token: "boolean", foreground: "FF8C00" },  // Orange for booleans
        { token: "delimiter", foreground: "000000" }, // Black for delimiters
    ],
    colors: {
        "editor.background": "#ffffff",
        "editor.foreground": "#000000",
        "editorLineNumber.foreground": "#2B91AF",
        "editor.lineHighlightBackground": "#f0f0f0",
        "editor.lineHighlightBorder": "#f0f0f0",
        "editorGutter.background": "#f3f3f3",
        "editor.selectionBackground": "#add6ff",
        "editor.inactiveSelectionBackground": "#e5ebf1",
        "editorCursor.foreground": "#000000",
        "editorWhitespace.foreground": "#A0A0A0",
        "editorIndentGuide.background": "#D3D3D3",
        "editorIndentGuide.activeBackground": "#939393",
        "editorLineNumber.activeForeground": "#000000",
    }
});

monaco.editor.defineTheme("json-theme-dark", {
    base: "vs-dark",
    inherit: true,
    rules: [
        { token: "key", foreground: "CE9178" },      // Light red for keys
        { token: "value", foreground: "9CDCFE" },    // Light blue for values
        { token: "number", foreground: "B5CEA8" },   // Light green for numbers
        { token: "boolean", foreground: "FFB86C" },  // Light orange for booleans
        { token: "delimiter", foreground: "D4D4D4" }, // Light gray for delimiters
    ],
    colors: {
        "editor.background": "#1E1E1E",
        "editor.foreground": "#D4D4D4",
        "editorLineNumber.foreground": "#858585",
        "editor.lineHighlightBackground": "#2A2D2E",
        "editor.lineHighlightBorder": "#2A2D2E",
        "editorGutter.background": "#1E1E1E",
        "editor.selectionBackground": "#264F78",
        "editor.inactiveSelectionBackground": "#3A3D41",
        "editorCursor.foreground": "#FFFFFF",
        "editorWhitespace.foreground": "#404040",
        "editorIndentGuide.background": "#404040",
        "editorIndentGuide.activeBackground": "#707070",
        "editorLineNumber.activeForeground": "#FFFFFF",
    }
});

monaco.languages.register({ id: "jsonWithCustomColors" });

monaco.languages.setMonarchTokensProvider("jsonWithCustomColors", {
    tokenizer: {
        root: [
            [/\"([a-zA-Z0-9_]+)\"\s*:/, { token: "key" }],
            [/\"([^\"\\]*(?:\\.[^\"\\]*)*)\"/, { token: "value" }],
            [/(\d+)/, { token: "number" }],
            [/\b(true|false|null)\b/, { token: "boolean" }],
            [/[{}[\]]/, { token: "delimiter" }],
            [/,/, { token: "delimiter" }],
        ],
    },
});

const RawJson = ({ body, onBodyChange }) => {
    const { openSnackbar } = useSnackbar();
    const [bodyRaw, setBodyRaw] = useState(JSON.stringify(body, undefined, 4));
    const [error, setError] = useState(null);
    const editorRef = useRef(null);
    const monacoInstance = useRef(null);
    const editorInstance = useRef(null);

    const { isDarkMode } = useTheme();

    const editorDidMount = (editor, monaco) => {
        monacoInstance.current = monaco;
        editorInstance.current = editor;
    };

    useEffect(() => {
        setBodyRaw(JSON.stringify(body, undefined, 4));
    }, [body]);

    const handleOutsideClick = (event) => {
        if (editorRef.current && !editorRef.current.contains(event.target)) {
            try {
                const jsonObject = JSON.parse(bodyRaw.replace(/,\s*([}\]])/g, '$1'));
                onBodyChange(jsonObject);
            } catch (err) {
                openSnackbar({ message: "Invalid JSON format. Please correct it before saving.", severity: 'error' });
            }
        }
    };

    useEffect(() => {
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, [bodyRaw]);

    return (
        <Box
            ref={editorRef}
            sx={{
                overflow: "auto",
                border: "1px solid var(--borderColor)",
                margin: "10px 0",
                borderRadius: "5px",
                position: "relative",
            }}
        >
            <MonacoEditorWithFullscreen
                language="jsonWithCustomColors"
                height="calc(100vh - 300px)"
                theme={isDarkMode ? "json-theme-dark" : "json-theme-light"}
                value={bodyRaw}
                onChange={(newBodyRaw) => {
                    setBodyRaw(newBodyRaw);
                    setError(null);
                }}
                editorDidMount={editorDidMount}
                options={{
                    automaticLayout: true,
                    wordWrap: "on",
                    minimap: { enabled: false },
                    lineNumbers: "on",
                    tabSize: 2,
                    fontSize: 13,
                    fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                    scrollBeyondLastLine: false,
                    scrollbar: {
                        vertical: 'visible',
                        horizontal: 'visible',
                        verticalScrollbarSize: 14,
                        horizontalScrollbarSize: 14,
                        verticalSliderSize: 14,
                        horizontalSliderSize: 14,
                        useShadows: false,
                        arrowSize: 14,
                    },
                    overviewRulerBorder: false,
                    hideCursorInOverviewRuler: true,
                    renderLineHighlight: 'all',
                    matchBrackets: 'always',
                    autoClosingBrackets: 'always',
                    autoClosingQuotes: 'always',
                    formatOnPaste: true,
                    formatOnType: true,
                }}
            />

            <Snackbar
                open={!!error}
                autoHideDuration={6000}
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    severity="error"
                    onClose={() => setError(null)}
                    sx={{ width: '100%' }}
                >
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default RawJson;
